<template>
  <div class="organization-report">
    <router-link class="breadcrumb" :to="{ name: routes.admin.adminDashboard }">
      <span class="breadcrumb-icon">←</span>
      {{ $adminT('adminAll.back_to_operations_console_6f9440', 'Back to Operations Console') }}
    </router-link>
    <h1>{{ $adminT('adminAll.organization_report_095e68', 'Organization Report') }}</h1>
    <h2 v-if="!isLoading">{{ $adminT('adminAll.organization_6da949', 'Organization:') }} {{ organization.legalName || '—' }}</h2>
    <div v-else class="heading-skeleton heading-skeleton--secondary" />
    <h4 v-if="!isLoading">{{ $adminT('adminAll.period_od_946a13', 'Period od') }} {{ formatDate(orderFrom) }} {{ $adminT('adminAll.do_eadcd9', 'do') }} {{ formatDate(orderTo) }}</h4>
    <div v-else class="heading-skeleton heading-skeleton--tertiary" />

    <div v-if="reportError" class="state-error">{{ adminAllMessage(reportError) }}</div>
    <template v-else-if="isLoading">
      <h3>{{ $adminT('adminAll.naknade_za_izdavanje_ulaznica_feb6de', 'Naknade Za Izdavanje Ulaznica') }}</h3>
      <div class="table-wrapper">
        <table class="report-table report-table--loading">
          <thead>
            <tr>
              <th v-for="column in 8" :key="`fixed-head-${column}`">
                <div class="table-skeleton-line table-skeleton-line--header" />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in skeletonRows" :key="`fixed-row-${row}`">
              <td v-for="column in 8" :key="`fixed-row-${row}-${column}`">
                <div class="table-skeleton-line" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3>{{ $adminT('adminAll.ostale_naknade_a1656d', 'Ostale Naknade') }}</h3>
      <div class="table-wrapper">
        <table class="report-table report-table--loading report-table--compact">
          <thead>
            <tr>
              <th v-for="column in 5" :key="`purchase-head-${column}`">
                <div class="table-skeleton-line table-skeleton-line--header" />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in skeletonRows" :key="`purchase-row-${row}`">
              <td v-for="column in 5" :key="`purchase-row-${row}-${column}`">
                <div class="table-skeleton-line" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
    <template v-else-if="!reportData.length && !purchaseSummaries.length">
      <div class="state-info">{{ $adminT('adminAll.no_report_data_found_for_the_selected_period_f2ec30', 'No report data found for the selected period.') }}</div>
    </template>
    <template v-else>
      <h3>{{ $adminT('adminAll.naknade_za_izdavanje_ulaznica_feb6de', 'Naknade Za Izdavanje Ulaznica') }}</h3>
      <div class="table-wrapper">
        <table class="report-table">
          <thead>
            <tr>
              <th>{{ $adminT('adminAll.doga_aj_161b6b', 'Događaj') }}</th>
              <th>{{ $adminT('adminAll.organizator_86301b', 'Organizator') }}</th>
              <th>{{ $adminT('adminAll.prosj_naknada_03d5de', 'Prosj. Naknada (€)') }}</th>
              <th>{{ $adminT('adminAll.kolicina_online_offline_ac0ef7', 'Količina online + offline') }}</th>
              <th>{{ $adminT('adminAll.kolicina_online_4aaa9e', 'Količina online') }}</th>
              <th>{{ $adminT('adminAll.kolicina_offline_73582d', 'Količina offline') }}</th>
              <th>{{ $adminT('adminAll.ukupno_naknada_057c2c', 'Ukupno Naknada (€)') }}</th>
              <th>{{ $adminT('adminAll.pdv_na_naknadu_b7b505', 'PDV na Naknadu (€)') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in reportData" :key="item.eventId">
              <td>{{ item.eventTitle }}</td>
              <td>{{ item.organization }}</td>
              <td :data-label="$adminT('adminAll.prosj_naknada_03d5de', 'Prosj. Naknada (€)')">
                {{
                  formatCurrency(
                    item.totalCoreEventEarningsWithoutVat / item.coreEventFeeTicketCount || 0,
                  )
                }}
              </td>
              <td>{{ item.ticketCount }}</td>
              <td>{{ item.online }}</td>
              <td>{{ item.offline }}</td>
              <td>{{ formatCurrency(item.totalCoreEventEarningsWithoutVat) }}</td>
              <td>{{ formatCurrency(item.totalCoreEventEarningsWithoutVat * 0.25) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3>{{ $adminT('adminAll.postoci_od_ulaznica_774cd6', 'Postoci od ulaznica') }}</h3>
      <div class="table-wrapper">
        <table class="report-table report-table--compact">
          <thead>
            <tr>
              <th>{{ $adminT('adminAll.event_ad8919', 'Event') }}</th>
              <th>{{ $adminT('adminAll.sold_count_e7ef63', 'Sold Count') }}</th>
              <th>{{ $adminT('adminAll.total_sold_amount_4a1339', 'Total Sold Amount (€)') }}</th>
              <th>{{ $adminT('adminAll.total_coreevent_fee_65f7f6', 'Total CoreEvent Fee (€)') }}</th>
              <th>{{ $adminT('adminAll.total_coreevent_fee_with_vat_cf6518', 'Total CoreEvent Fee with VAT (€)') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="summary in purchaseSummaries" :key="summary.event.id">
              <td>{{ summary.event.title }}</td>
              <td>{{ summary.totalSoldCount }}</td>
              <td>{{ formatCurrency(summary.totalSoldAmount) }}</td>
              <td>{{ formatCurrency(summary.totalCoreEventFee) }}</td>
              <td>{{ formatCurrency(summary.totalCoreEventFeeWithVAT) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="totals">
        <h3 class="total-row">
          <span>{{ $adminT('adminAll.naknade_za_izdavanje_ulaznica_fc61b4', 'Naknade Za Izdavanje Ulaznica:') }}</span>
          <span>{{ formatCurrency(totalCoreEventTicketFeeEarnings) }} €</span>
        </h3>
        <h3 class="total-row">
          <span>{{ $adminT('adminAll.naknade_postotak_od_ulaznica_4926cb', 'Naknade Postotak Od Ulaznica:') }}</span>
          <span>{{ formatCurrency(totalCoreEventEarnings - totalCoreEventTicketFeeEarnings) }} €</span>
        </h3>
        <h3 class="total-row">
          <span>{{ $adminT('adminAll.ukupno_naknada_776c8f', 'Ukupno Naknada:') }}</span>
          <span>{{ formatCurrency(totalCoreEventEarnings) }} €</span>
        </h3>
        <h3 class="total-row">
          <span>{{ $adminT('adminAll.ukupno_pdv_na_naknade_ade48e', 'Ukupno PDV na Naknade:') }}</span>
          <span>{{ formatCurrency(totalCoreEventEarnings * 0.25) }} €</span>
        </h3>
        <hr>
        <h3 class="total-row total-row--strong">
          <span>{{ $adminT('adminAll.ukupno_naknada_s_pdv_om_8845ff', 'Ukupno Naknada s PDV-om:') }}</span>
          <span>{{ formatCurrency(totalCoreEventEarnings * 1.25) }} €</span>
        </h3>
        <hr class="totals-divider">
        <h3 class="total-row">
          <span>{{ $adminT('adminAll.ukupan_prihod_organizatora_7dd421', 'Ukupan prihod organizatora') }} {{ organization.legalName }}</span>
          <span>{{ formatCurrency(totalOrganizerEarnings) }} €</span>
        </h3>
        <h3 class="total-row">
          <span>{{ $adminT('adminAll.ukupan_prihod_organizatora_na_vlastitoj_blagajni_6456ee', 'Ukupan prihod organizatora na vlastitoj blagajni') }}</span>
          <span>{{ formatCurrency(getExcludedCashRegisterSale()) }} €</span>
        </h3>
        <h3 class="total-row">
          <span>
            {{ $adminT('adminAll.sveukupno_971025', 'Sveukupno:') }}<br>
            <template v-if="getExcludedCashRegisterSale() > 0">
              {{ $adminT('adminAll.prihod_naknade_od_postotaka_pdv_prihod_na_vlastitoj_blagaj_c45ab0', '(prihod - (naknade od postotaka + pdv) - prihod na vlastitoj blagajni - povrati - ((povrati * 2,5%) + pdv))') }}
            </template>
            <template v-else>
              {{ $adminT('adminAll.prihod_naknade_od_postotaka_pdv_povrati_povrati_2_5_pdv_165861', '(prihod - (naknade od postotaka + pdv) - povrati - ((povrati * 2,5%) + pdv))') }}
            </template>
          </span>
          <span>{{ formatCurrency(amountToPay) }} €</span>
        </h3>
        <p v-if="getExcludedCashRegisterSale() > 0" class="cash-register-note">
          {{ $adminT('adminAll.napomena_prihod_od_prodaje_putem_blagajne_organizatora_nij_9de182', 'Napomena: Prihod od prodaje putem blagajne organizatora nije predmet isplate. Na ostvareni iznos obračunata je pripadajuća provizija.') }}
        </p>
      </div>

      <div class="payment-section">
        <h3>{{ $adminT('adminAll.uplata_29ce17', 'Uplata') }}</h3>
        <canvas id="paymentQR" />
      </div>
    </template>
  </div>
</template>

<script>
import AdminAllMessages from '@/components/admin/AdminAllMessages'
import moment from 'moment'
import gql from 'graphql-tag'

const PRINT_SCROLL_SELECTOR = '.table-wrapper'

const TVORNICA_KULTURE_ORGANIZATION_IDS = [319, 357, 405, 635]

export default {
  mixins: [AdminAllMessages],
  provide: { adminLocalization: true },
  name: 'organization-report',
  data() {
    return {
      organizationSlug: '',
      organizationName: '',
      orderFrom: '',
      orderTo: '',
      purchaseSummaries: [],
      totalCoreEventEarnings: 0,
      totalClientsCashRegisterSale: 0,
      totalOrganizerEarnings: 0,
      totalCoreEventTicketFeeEarnings: 0,
      organization: {},
      refundInfo: {
        amount: 0,
        coreEventRefundFee: 0,
      },
      reportData: [],
      amountToPay: 0, // Dodali smo ovu varijablu za iznos koji treba isplatiti
      paymentScriptsPromise: null,
      reportError: null,
      printScrollPositions: [],
      beforePrintHandler: null,
      afterPrintHandler: null,
      printMediaQuery: null,
      printMediaQueryHandler: null,
    }
  },
  computed: {
    isLoading() {
      return !!(this.$apollo && this.$apollo.queries && this.$apollo.queries.getOrganizerReport
        && this.$apollo.queries.getOrganizerReport.loading)
    },
    skeletonRows() {
      return 5
    },
  },
  mounted() {
    if (typeof document !== 'undefined') {
      document.body.classList.add('admin-report-print-layout')
    }
    this.setupPrintHandlers()
    this.ensurePaymentScripts()
  },
  beforeDestroy() {
    this.cleanupPrintHandlers()
    if (typeof document !== 'undefined') {
      document.body.classList.remove('admin-report-print-layout')
    }
  },
  created() {
    this.organizationSlug = this.$route.params.organizationSlug
    this.organizationName = this.getOrganizationName(this.organizationSlug)
    this.orderFrom = new Date(decodeURIComponent(this.$route.query.from))
    this.orderTo = new Date(decodeURIComponent(this.$route.query.to))
  },
  methods: {
    formatDate(date) {
      return moment(date).format('DD.MM.YYYY')
    },
    formatCurrency(value) {
      return parseFloat(value).toFixed(2)
    },
    getPrintScrollContainers() {
      if (typeof document === 'undefined') {
        return []
      }

      return Array.from(document.querySelectorAll(PRINT_SCROLL_SELECTOR))
    },
    preparePrintLayout() {
      this.printScrollPositions = this.getPrintScrollContainers().map(container => {
        const position = {
          container,
          top: container.scrollTop,
          left: container.scrollLeft,
        }

        container.scrollTop = 0
        container.scrollLeft = 0

        return position
      })
    },
    restorePrintLayout() {
      this.printScrollPositions.forEach(({ container, top, left }) => {
        if (!container || !container.isConnected) {
          return
        }

        container.scrollTop = top
        container.scrollLeft = left
      })
      this.printScrollPositions = []
    },
    setupPrintHandlers() {
      if (typeof window === 'undefined') {
        return
      }

      this.beforePrintHandler = () => this.preparePrintLayout()
      this.afterPrintHandler = () => this.restorePrintLayout()
      window.addEventListener('beforeprint', this.beforePrintHandler)
      window.addEventListener('afterprint', this.afterPrintHandler)

      if (typeof window.matchMedia !== 'function') {
        return
      }

      this.printMediaQuery = window.matchMedia('print')
      this.printMediaQueryHandler = event => {
        if (event.matches) {
          this.preparePrintLayout()
        } else {
          this.restorePrintLayout()
        }
      }

      if (typeof this.printMediaQuery.addEventListener === 'function') {
        this.printMediaQuery.addEventListener('change', this.printMediaQueryHandler)
      } else if (typeof this.printMediaQuery.addListener === 'function') {
        this.printMediaQuery.addListener(this.printMediaQueryHandler)
      }
    },
    cleanupPrintHandlers() {
      if (typeof window !== 'undefined') {
        if (this.beforePrintHandler) {
          window.removeEventListener('beforeprint', this.beforePrintHandler)
        }
        if (this.afterPrintHandler) {
          window.removeEventListener('afterprint', this.afterPrintHandler)
        }
      }

      if (this.printMediaQuery && this.printMediaQueryHandler) {
        if (typeof this.printMediaQuery.removeEventListener === 'function') {
          this.printMediaQuery.removeEventListener('change', this.printMediaQueryHandler)
        } else if (typeof this.printMediaQuery.removeListener === 'function') {
          this.printMediaQuery.removeListener(this.printMediaQueryHandler)
        }
      }

      this.restorePrintLayout()
      this.beforePrintHandler = null
      this.afterPrintHandler = null
      this.printMediaQuery = null
      this.printMediaQueryHandler = null
    },
    getOrganizationName(slug) {
      return slug.replace(/-/g, ' ').toUpperCase()
    },
    isTvornicaKultureOrganization() {
      const organizationId = Number(this.organization.id)

      return TVORNICA_KULTURE_ORGANIZATION_IDS.includes(organizationId)
    },
    getExcludedCashRegisterSale() {
      if (this.isTvornicaKultureOrganization()) {
        return 0
      }

      return Number(this.totalClientsCashRegisterSale || 0)
    },
    parseEventIdsParam(value) {
      const raw = Array.isArray(value) ? value.join(',') : String(value || '')
      const tokens = raw
        .split(/[\s,;]+/)
        .map(token => token.trim())
        .filter(Boolean)

      if (!tokens.length) {
        return []
      }

      return Array.from(
        new Set(
          tokens
            .filter(token => /^[0-9]+$/.test(token))
            .map(token => Number(token))
            .filter(id => Number.isInteger(id) && id > 0),
        ),
      )
    },
    ensurePaymentScripts() {
      if (window.BarcodePayment && window.PDF417) {
        return Promise.resolve(true)
      }
      if (this.paymentScriptsPromise) {
        return this.paymentScriptsPromise
      }

      const loadScript = src =>
        new Promise((resolve, reject) => {
          const script = document.createElement('script')
          script.setAttribute('src', src)
          script.onload = () => resolve()
          script.onerror = () => reject(new Error(`Failed to load script: ${src}`))
          document.head.appendChild(script)
        })

      this.paymentScriptsPromise = (async () => {
        try {
          await loadScript('https://code.jquery.com/jquery-latest.min.js')
          await loadScript('https://bikonja.github.io/generator-barkoda-uplatnica/bcmath-min.js')
          await Promise.all([
            loadScript(
              'https://rawcdn.githack.com/Bikonja/generator-barkoda-uplatnica/master/BarcodePayment.js',
            ),
            loadScript('https://bikonja.github.io/generator-barkoda-uplatnica/pdf417-min.js'),
          ])

          return !!(window.BarcodePayment && window.PDF417)
        } catch (error) {
          console.error('Failed to load payment scripts', error)

          return false
        }
      })()

      return this.paymentScriptsPromise
    },
    async generatePaymentQR() {
      const scriptsReady = await this.ensurePaymentScripts()
      if (!scriptsReady) {
        console.error('Payment scripts not available for barcode generation')

        return
      }

      window.BarcodePayment.Init({
        ValidateIBAN: false,
        ValidateModelPozivNaBroj: false,
      })

      // Calculate the amount to be paid
      const totalCoreEventPercentageFee =
        this.totalCoreEventEarnings - this.totalCoreEventTicketFeeEarnings
      const totalFeeWithVAT = totalCoreEventPercentageFee * 1.25
      const refundFeeWithVAT =
        ((this.refundInfo && this.refundInfo.coreEventRefundFee) || 0) * 1.25
      const excludedCashRegisterSale = this.getExcludedCashRegisterSale()
      const amount =
        this.totalOrganizerEarnings -
        totalFeeWithVAT -
        excludedCashRegisterSale -
        ((this.refundInfo && this.refundInfo.amount) || 0) -
        refundFeeWithVAT
      this.amountToPay = amount // Store amount for display in totals

      // Format amount with comma as decimal separator
      const amountFormatted = amount.toFixed(2).replace('.', ',')

      // Payment description
      const paymentDescription = `Uplata ${this.formatDate(this.orderFrom)} do ${this.formatDate(
        this.orderTo,
      )}`

      // Reference number (today's date in format DDMMYYYY)
      const referenceNumber = moment().format('DDMMYYYY')
      // Prepare payment parameters
      const paymentParams = new window.BarcodePayment.PaymentParams()

      paymentParams.Iznos = amountFormatted
      paymentParams.ImePlatitelja = ''
      paymentParams.AdresaPlatitelja = ''
      paymentParams.SjedistePlatitelja = ''
      paymentParams.Primatelj = ''
      paymentParams.AdresaPrimatelja = ''
      paymentParams.SjedistePrimatelja = ''
      paymentParams.IBAN = this.organization.IBAN
      paymentParams.ModelPlacanja = '00'
      paymentParams.PozivNaBroj = referenceNumber
      paymentParams.SifraNamjene = ''
      paymentParams.OpisPlacanja = paymentDescription

      // Encode payment parameters
      const textToEncode = window.BarcodePayment.GetEncodedText(paymentParams)

      if (
        textToEncode === window.BarcodePayment.ResultCode.InvalidContent ||
        textToEncode === window.BarcodePayment.ResultCode.InvalidObject ||
        !textToEncode
      ) {
        console.error('Invalid content for barcode generation')

        return
      }

      // Generate the barcode
      window.PDF417.init(textToEncode)
      const barcode = window.PDF417.getBarcodeArray()
      // Prepare the canvas
      const canvas = document.getElementById('paymentQR')
      if (!canvas) {
        console.error('Payment QR canvas not found')

        return
      }
      const ctx = canvas.getContext('2d')

      const moduleWidth = 2
      const moduleHeight = 2

      canvas.width = barcode.num_cols * moduleWidth
      canvas.height = barcode.num_rows * moduleHeight

      ctx.clearRect(0, 0, canvas.width, canvas.height)

      ctx.fillStyle = 'black'

      for (let row = 0; row < barcode.num_rows; row++) {
        for (let col = 0; col < barcode.num_cols; col++) {
          if (barcode.bcode[row][col] === '1') {
            ctx.fillRect(col * moduleWidth, row * moduleHeight, moduleWidth, moduleHeight)
          }
        }
      }
    },
  },
  apollo: {
    getOrganizerReport: {
      query: gql`
        query getOrganizerReport(
          $organizationSlug: String!
          $orderFrom: DateTime!
          $orderTo: DateTime!
          $eventId: Int
          $eventIds: [Int!]
          $eventTemplateId: Int
        ) {
          getOrganizerReport(
            organizationSlug: $organizationSlug
            orderFrom: $orderFrom
            orderTo: $orderTo
            eventId: $eventId
            eventIds: $eventIds
            eventTemplateId: $eventTemplateId
          )
        }
      `,
      variables() {
        const eventIdRaw = this.$route.query.eventId
        const eventIdsRaw = this.$route.query.eventIds
        const eventTemplateIdRaw = this.$route.query.eventTemplateId
        const eventId = eventIdRaw ? Number(eventIdRaw) : null
        const eventTemplateId = eventTemplateIdRaw ? Number(eventTemplateIdRaw) : null
        const eventIds = this.parseEventIdsParam(eventIdsRaw)
        const mergedEventIds = Array.from(
          new Set([
            ...eventIds,
            ...(Number.isInteger(eventId) && eventId > 0 ? [eventId] : []),
          ]),
        )

        return {
          organizationSlug: this.organizationSlug,
          orderFrom: this.orderFrom,
          orderTo: this.orderTo,
          eventId: mergedEventIds.length === 1 ? mergedEventIds[0] : null,
          eventIds: mergedEventIds.length ? mergedEventIds : null,
          eventTemplateId: Number.isFinite(eventTemplateId) ? eventTemplateId : null,
        }
      },
      result({ data }) {
        if (data && data.getOrganizerReport) {
          this.reportError = null
          const report = data.getOrganizerReport
          this.reportData = report.reportData
          this.totalOrganizerEarnings = report.totalOrganizerEarnings
          this.organization = report.organization
          this.totalClientsCashRegisterSale = report.totalClientsCashRegisterSale
          this.purchaseSummaries = report.purchaseSummaries
          this.totalCoreEventEarnings = report.totalCoreEventEarnings
          this.totalCoreEventTicketFeeEarnings = Number(
            report.totalCoreEventTicketFeeEarnings != null
              ? report.totalCoreEventTicketFeeEarnings
              : report.totalCoreEventFixedFeeEarnings || 0,
          )
          this.refundInfo = report.refundInfo || {
            amount: 0,
            coreEventRefundFee: 0,
          }

          this.$nextTick(() => {
            this.generatePaymentQR()
          })
        }
      },
      error(error) {
        this.reportError = (error && error.message) || this.adminAllNotice('Failed to load organizer report.')
      },
    },
  },
}
</script>

<style scoped>
.breadcrumb {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  color: #2563eb;
  font-weight: 700;
  padding: 0;
  margin: 0 0 12px;
  text-decoration: none;
}
.breadcrumb-icon {
  font-size: 16px;
}
rect {
  fill: black;
}

.organization-report {
  background: white;
  max-width: min(100%, 21cm);
  margin: auto;
  display: block;
  padding: 24px;
  box-sizing: border-box;
}

.heading-skeleton,
.table-skeleton-line {
  border-radius: 999px;
  background: linear-gradient(90deg, #eef2f7 25%, #e2e8f0 37%, #eef2f7 63%);
  background-size: 400% 100%;
  animation: organization-report-shimmer 1.4s ease-in-out infinite;
}

.heading-skeleton {
  margin-bottom: 12px;
}

.heading-skeleton--secondary {
  width: min(100%, 320px);
  height: 30px;
}

.heading-skeleton--tertiary {
  width: min(100%, 280px);
  height: 20px;
}

@media print {
  .breadcrumb {
    display: none !important;
  }

  .payment-section {
    display: none !important;
  }

  .table-wrapper {
    max-height: none !important;
    height: auto !important;
    overflow: visible !important;
  }

  .report-table thead th {
    position: static;
    box-shadow: none;
  }

  .report-table thead,
  .report-table tfoot {
    display: table-row-group !important;
  }

  .report-table tr,
  .report-table td,
  .report-table th {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }
}

.totals {
  margin-top: 24px;
  margin-left: auto;
  width: min(100%, 520px);
}

.total-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.total-row--strong {
  font-weight: 700;
}

.total-row > span:last-child {
  text-align: right;
  white-space: nowrap;
}

.totals-divider {
  margin: 30px 0;
}

.cash-register-note {
  margin-top: 10px;
  font-size: 12px;
  color: #374151;
  max-width: 720px;
}

.state-info,
.state-error {
  margin-top: 16px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #fff;
}

.state-error {
  border-color: #fecaca;
  background: #fef2f2;
  color: #b91c1c;
}

.table-wrapper {
  width: 100%;
  max-height: min(70vh, 640px);
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 24px;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  background: #fff;
}

.report-table {
  width: 100%;
  min-width: 820px;
  color: #333;
  background: white;
  font-size: 12pt;
  border-collapse: collapse;
}

.report-table--compact {
  min-width: 680px;
}

.report-table thead th {
  color: #475569;
  background: #eef2f7;
  position: sticky;
  top: 0;
  z-index: 2;
  background-clip: padding-box;
  box-shadow: inset 0 -1px 0 #d1d5db;
}

.report-table th,
.report-table td {
  padding: 0.5em;
  border: 1px solid lightgrey;
  text-align: left;
  vertical-align: top;
}

.report-table--loading td {
  padding-top: 0.75em;
  padding-bottom: 0.75em;
}

.table-skeleton-line {
  width: 100%;
  height: 16px;
}

.table-skeleton-line--header {
  height: 14px;
}

[data-table-theme*='zebra'] tbody tr:nth-of-type(odd) {
  background: rgba(0, 0, 0, 0.05);
}

[data-table-theme*='zebra'][data-table-theme*='dark'] tbody tr:nth-of-type(odd) {
  background: rgba(255, 255, 255, 0.05);
}

/* Dark Style */
[data-table-theme*='dark'] {
  color: #ddd;
  background: #333;
  font-size: 12pt;
  border-collapse: collapse;
}

[data-table-theme*='dark'] thead th,
[data-table-theme*='dark'] tfoot th {
  color: #aaa;
  background: rgba(0255, 255, 255, 0.15);
}

[data-table-theme*='dark'] caption {
  padding: 0.5em;
}

[data-table-theme*='dark'] th,
[data-table-theme*='dark'] td {
  padding: 0.5em;
  border: 1px solid grey;
}

.payment-section {
  margin-top: 30px;
}

#paymentQR {
  max-width: 300px;
  max-height: 300px;
}

@keyframes organization-report-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

@media (max-width: 768px) {
  .organization-report {
    padding: 14px;
  }

  .total-row {
    flex-direction: column;
    gap: 4px;
  }

  .total-row > span:last-child {
    text-align: left;
    white-space: normal;
  }
}
</style>

<style>
@media print {
  body.admin-report-print-layout .header,
  body.admin-report-print-layout .footer,
  body.admin-report-print-layout .application-toolbar,
  body.admin-report-print-layout .application-toolbar-zkm,
  body.admin-report-print-layout .back-button-zkm {
    display: none !important;
  }
}
</style>
